import { device } from 'utils/device';
import ComboCounter from 'utils/ComboCounter';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * OrganicLinesBasePhase — the shared machinery of the two organic-lines
 * phases: FORK of the swarm/target arms both reference stages duplicate
 * (organicLinesStart, the followParticle assignment, the five target-particle
 * blocks) plus the follow-swarm lane plumbing.
 *
 * VISUAL: five bright breathing "anchor" dots hold court in the box while
 * the rest of the pool swarms around them on fixed per-particle angles at a
 * choreographed radius — whoozy vector movement smears the followers into
 * organic light lines; the ComboCounter timeline (each concrete phase's)
 * then walks the anchors and the follow radius through formations.
 *
 * The choreography ComboCounter is PER PHASE here where the reference
 * shares one department counter — only one organic stage is ever active at
 * a time and the reference clears the shared counter at every stage change,
 * so a per-phase counter cleared in hide() is equivalent.
 *
 * Follow mechanics: followIdx lanes + the progressParallax kernel's follow
 * branch (prev-frame rel read — see the kernel header for the one-frame-lag
 * note). The department resets followIdx across EVERY phase transition (the
 * reference's _reset), so hide() here doesn't need to.
 */
export abstract class OrganicLinesBasePhase implements DepartmentPhase {
	public abstract readonly name: string;

	protected _ctx: SpacesContext;
	protected _comboCounter: ComboCounter = new ComboCounter();
	protected readonly _totalTargets = 5;

	constructor(ctx: SpacesContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup.
	}

	public abstract start(): void;

	public tick(): void {
		//The reference ticks the shared combo counter at the top of
		//_counter(); phase-local here (see the class header):
		this._comboCounter.tick();
	}

	public hide(): void {
		this._comboCounter.clear();
	}

	public reset(): void {
		//No persistent phase state beyond the cleared counter.
	}

	//Shared arms (verbatim reference ports)
	//-----------------------------------------------------------------------------------------------

	/** organicLinesStart — the swarm arm, lane-for-line. */
	protected organicLineStartLane(lane: number): void {
		const ctx = this._ctx;
		const core = ctx.core;

		core.tScale[lane] = 0.1 + Math.random() * 0.1; //dead write (hardSetScale overwrites) — roll kept
		//hardSetScale(0.1 + rand * 0.04):
		const hard = 0.1 + Math.random() * 0.04;
		core.scale[lane] = hard;
		core.tScale[lane] = hard;
		core.tScaleSaved[lane] = hard;

		core.tAlpha[lane] = 0.35 + Math.random() * 0.4;
		//stopBreathing():
		core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;
		ctx.deactivateAll(lane);

		const ratio = (device.innerHeight / device.innerWidth) * 1.5;
		const xIncreaseRate = 0.06 + Math.random() * 0.2;
		const xMaxForce = 2 + Math.random() * 5;

		ctx.setEaseXY(lane, 1 + Math.random() * 6, 1 + Math.random() * 3);

		//setVectorStyleMovement(true, xConfig, yConfig):
		writeVectorConfig(core, lane, VEC_X, true, xIncreaseRate, xMaxForce, 0.5);
		writeVectorConfig(core, lane, VEC_Y, true, xIncreaseRate * ratio, xMaxForce * ratio, 0.5);

		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 100;
		core.repellPower[lane] = 100 + Math.random() * 20;
	}

	/** The follow assignment (position.followParticle setter: random angle
	 *  roll + the 0.009 radius default) + the explicit radius write. */
	protected followLane(lane: number, targetLane: number, radius: number): void {
		const core = this._ctx.core;
		core.followIdx[lane] = targetLane;
		core.followAngleDeg[lane] = Math.random() * 360;
		core.followRadius[lane] = 0.009;
		core.followRadius[lane] = radius;
	}

	/** The organic target-particle arm (park at rel {x, y}, breathe). */
	protected organicTargetLane(lane: number, x: number, y: number): void {
		const ctx = this._ctx;
		const core = ctx.core;

		ctx.setEaseXY(lane, 20, 10);

		ctx.deactivateAll(lane);
		core.stateFlags[lane] |= FLAG_ACTIVE;
		//position.set({x, y}) — SOFT:
		core.relX[lane] = x;
		core.relY[lane] = y;

		core.tAlpha[lane] = 0.8;
		core.tScale[lane] = 0.18;
		core.blendAdd[lane] = 1;

		//startBreathing(0.2, 0.05*0.2, 1*0.2, [-1,1], rand*0.4*0.2, ±1):
		const baseScale = 0.2;
		const startChangeValue = Math.random() * 0.4 * baseScale;
		const startDirection = Math.random() < 0.5 ? -1 : 1;
		core.breathBase[lane] = baseScale;
		core.breathChange[lane] = 0.05 * baseScale;
		core.breathMax[lane] = 1 * baseScale;
		core.breathAdd[lane] = startChangeValue * startDirection;
		core.breathDir[lane] = startDirection;
		core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;
	}

	/** The swarm follow roll: 5 equal bands over the 5 target lanes. */
	protected rollFollowTarget(): number {
		const rand = Math.random();
		if (rand <= 0.2) return this._ctx.range.start;
		if (rand <= 0.4) return this._ctx.range.start + 1;
		if (rand <= 0.6) return this._ctx.range.start + 2;
		if (rand <= 0.8) return this._ctx.range.start + 3;
		return this._ctx.range.start + 4;
	}

	/** Batch follow-radius write over the swarm lanes. */
	protected setSwarmRadius(radius: number): void {
		const core = this._ctx.core;
		for (let n = this._totalTargets; n < this._ctx.activeCount; n++) {
			core.followRadius[this._ctx.range.start + n] = radius;
		}
	}
}
